import { useForm, useStore } from '@tanstack/react-form'
import {
  normalizeLinkedInUrl,
  validateHttpsUrl,
  validateLinkedInUrl,
  validateMonth,
  validatePositiveAmount,
  validateRequired,
} from '@workspace/seedforge-shared'
import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { Switch } from '@workspace/ui/components/switch'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn, formatDate, getInitials } from '@workspace/ui/lib/utils'
import { Eye, FileText, Plus, RefreshCw, Upload, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { DataRoomDocument, DataRoomTeamMember } from './types.js'

// ─── Types ────────────────────────────────────────────────────────────────

export interface ProfileEditFormData {
  name: string
  sector: string
  sectorLabel?: string
  stage: string
  location: string
  founded: string
  website: string
  description: string
  tags: string[]
  isFundraising: boolean
  fundraisingRound: string
  fundraisingTarget: string
  team: DataRoomTeamMember[]
  documents: DataRoomDocument[]
}

/** What an external action bar can do to this form. */
export interface ProfileEditFormActions {
  submit: () => void
  discard: () => void
}

export interface ProfileEditFormProps {
  /** Initial/current data to populate the form */
  data: ProfileEditFormData
  /** Called with all form data when user clicks submit */
  onSubmit?: (data: ProfileEditFormData) => void | Promise<void>
  /** Called when user clicks discard */
  onDiscard?: () => void
  /** Upload files and return document entries */
  onUploadFiles?: (files: File[]) => Promise<DataRoomDocument[]>
  /** Called when user clicks Preview on a document */
  onPreviewDocument?: (doc: DataRoomDocument) => void
  /** Text shown on the primary action button */
  submitLabel?: string
  /** Message shown in the sticky bar */
  barMessage?: string
  /** Label for the secondary/discard button, null to hide */
  discardLabel?: string | null
  /**
   * Hide the sticky bar and drive Save/Cancel from outside — the founder
   * profile puts them in the page header with its other actions. The handlers
   * still live here, so there is exactly one implementation of saving.
   */
  hideActionBar?: boolean
  /** Receives the form's actions so an external bar can call them. */
  actionsRef?: { current: ProfileEditFormActions | null }
  /** Reports dirty/submitting so an external bar can label itself. */
  onStatusChange?: (status: { isDirty: boolean; isSubmitting: boolean }) => void
  /** Dropdown options for sector select */
  categoryOptions?: { value: string; label: string }[]
  /** Dropdown options for product stage */
  stageOptions?: { value: string; label: string }[]
  /** Dropdown options for fundraising round */
  raiseStageOptions?: { value: string; label: string }[]
  /** Sections to hide (e.g. hide documents in onboarding) */
  hiddenSections?: Set<string>
  /** Focus key from URL — scrolls to matching form field on mount */
  initialFocus?: string | null
  /** IDs of team members already persisted on the backend (used to gate the refresh button) */
  persistedMemberIds?: Set<string>
  /** Called when the user clicks "Refresh from LinkedIn" for a persisted team member */
  onRefreshMember?: (memberId: string) => void
  /** ID of the team member currently being refreshed (drives spinner/disabled state) */
  refreshingMemberId?: string | null
}

// ─── Sub-components ────────────────────────────────────────────────────────

function FormSection({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-medium text-muted-foreground">
      {children}
    </label>
  )
}

function Field({
  label,
  children,
  className,
  error,
}: {
  label: string
  children: React.ReactNode
  className?: string
  error?: string
}) {
  return (
    <div
      className={cn(
        'space-y-1.5',
        error &&
          '[&_input]:ring-destructive/50 [&_input]:ring-[1.5px] [&_textarea]:ring-destructive/50 [&_textarea]:ring-[1.5px]',
        className
      )}
    >
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  )
}

function Tag({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-[#f97316]/10 px-2 py-0.5 text-xs font-medium text-[#f97316]">
      {label}
      <button type="button" onClick={onRemove} className="text-[#f97316]/50 hover:text-[#f97316]">
        <X className="size-3" />
      </button>
    </span>
  )
}

const SECTOR_OPTIONS_FALLBACK = [
  { value: 'AI / Machine Learning', label: 'AI / Machine Learning' },
  { value: 'FinTech', label: 'FinTech' },
  { value: 'HealthTech', label: 'HealthTech' },
  { value: 'SaaS', label: 'SaaS' },
  { value: 'E-Commerce', label: 'E-Commerce' },
  { value: 'EdTech', label: 'EdTech' },
  { value: 'CleanTech', label: 'CleanTech' },
  { value: 'Marketplace', label: 'Marketplace' },
  { value: 'Developer Tools', label: 'Developer Tools' },
  { value: 'Other', label: 'Other' },
]

const STAGE_OPTIONS_FALLBACK = [
  { value: 'Idea', label: 'Idea' },
  { value: 'Proof of Concept', label: 'Proof of Concept' },
  { value: 'MVP', label: 'MVP' },
  { value: 'Advanced', label: 'Advanced' },
]

const RAISE_STAGE_OPTIONS_FALLBACK = [
  { value: 'Pre-Seed', label: 'Pre-Seed' },
  { value: 'Seed', label: 'Seed' },
  { value: 'Series A', label: 'Series A' },
  { value: 'Series B', label: 'Series B' },
  { value: 'Series C+', label: 'Series C+' },
]

function PillSelect({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(value === opt.value ? '' : opt.value)}
          className={cn(
            'rounded-full px-3 py-1 text-xs font-medium transition-colors',
            value === opt.value
              ? 'bg-[#f97316] text-white'
              : 'bg-card text-muted-foreground hover:text-white'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

/**
 * Month + year dropdowns producing a `YYYY-MM` value. Replaces a native
 * `<input type="month">`, which Safari renders as a plain text box (no picker),
 * forcing users to know the exact format. Two selects work cross-browser.
 */
function MonthYearSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [year, setYear] = useState(() => value.split('-')[0] ?? '')
  const [month, setMonth] = useState(() => value.split('-')[1] ?? '')

  // Sync in externally-provided values (e.g. AI prefill on onboarding confirm).
  // Ignore empty so partial selections the user is mid-way through aren't wiped.
  useEffect(() => {
    if (!value) return
    const [y, m] = value.split('-')
    setYear(y ?? '')
    setMonth(m ?? '')
  }, [value])

  const update = (nextYear: string, nextMonth: string) => {
    setYear(nextYear)
    setMonth(nextMonth)
    onChange(nextYear && nextMonth ? `${nextYear}-${nextMonth}` : '')
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: currentYear - 1949 }, (_, i) => String(currentYear - i))

  return (
    <div className="grid grid-cols-2 gap-2">
      <Select value={month} onValueChange={(m) => update(year, m)}>
        <SelectTrigger>
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {MONTH_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={year} onValueChange={(y) => update(y, month)}>
        <SelectTrigger>
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={y}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────────────

export function ProfileEditForm({
  data,
  onSubmit,
  onDiscard,
  onPreviewDocument,
  onUploadFiles,
  submitLabel = 'Save changes',
  barMessage = 'You have unsaved changes',
  discardLabel = 'Discard',
  hideActionBar = false,
  actionsRef,
  onStatusChange,
  categoryOptions,
  stageOptions,
  raiseStageOptions,
  hiddenSections,
  initialFocus,
  persistedMemberIds,
  onRefreshMember,
  refreshingMemberId,
}: ProfileEditFormProps) {
  const [newTag, setNewTag] = useState('')
  const [teamErrors, setTeamErrors] = useState<Record<string, string>>({})
  const focusApplied = useRef(false)
  const pitchDeckInputRef = useRef<HTMLInputElement>(null)
  const documentsInputRef = useRef<HTMLInputElement>(null)

  const validateAllTeamMembers = (members: DataRoomTeamMember[]): Record<string, string> => {
    const errors: Record<string, string> = {}
    for (const m of members) {
      const nameErr = validateRequired(m.name, 'Name')
      if (nameErr) errors[`name_${m.id}`] = nameErr
      const roleErr = validateRequired(m.role, 'Role')
      if (roleErr) errors[`role_${m.id}`] = roleErr
      const linkedInErr = validateLinkedInUrl(m.linkedInUrl ?? '')
      if (linkedInErr) errors[`linkedInUrl_${m.id}`] = linkedInErr
    }
    return errors
  }

  const form = useForm({
    defaultValues: data,
    onSubmit: ({ value }) => {
      const errors = validateAllTeamMembers(value.team)
      if (Object.keys(errors).length > 0) {
        setTeamErrors(errors)
        return
      }
      // Belt and braces on top of the on-blur rewrite: a founder who types a
      // handle and hits Save without leaving the field still gets a canonical
      // URL stored, so nothing downstream has to guess at the shape.
      const normalized = {
        ...value,
        team: value.team.map((member) => ({
          ...member,
          linkedInUrl: member.linkedInUrl
            ? (normalizeLinkedInUrl(member.linkedInUrl) ?? member.linkedInUrl)
            : member.linkedInUrl,
        })),
      }
      // Returned so form.state.isSubmitting stays true for the whole save.
      return onSubmit?.(normalized)
    },
  })

  // Reset form when parent data changes (e.g. after API save)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on data change only
  useEffect(() => {
    form.reset(data)
  }, [data])

  // Scroll to and focus the target section when initialFocus is set
  useEffect(() => {
    if (!initialFocus || focusApplied.current) return
    const isLinkedInFocus = initialFocus === 'team_linkedin'
    const focusKey = isLinkedInFocus ? 'team_members' : initialFocus
    const section = document.querySelector(`[data-focus-key="${focusKey}"]`)
    if (!section) return
    focusApplied.current = true

    // For team_linkedin, find the first empty LinkedIn input
    const targetEl = isLinkedInFocus
      ? Array.from(
          section.querySelectorAll<HTMLInputElement>('input[placeholder*="linkedin"]')
        ).find((el) => !el.value)
      : null

    const scrollTarget = targetEl?.closest('.rounded-lg') ?? section
    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })

    setTimeout(() => {
      if (targetEl) {
        targetEl.focus()
      } else {
        const input = section.querySelector<HTMLElement>(
          'input, textarea, select, [role="combobox"]'
        )
        input?.focus()
      }
    }, 400)
  }, [initialFocus])

  const handleDiscard = () => {
    form.reset()
    setNewTag('')
    onDiscard?.()
  }

  const handleFileUpload = async (files: FileList | null, type?: string) => {
    if (!files?.length || !onUploadFiles) return
    const uploaded = await onUploadFiles(Array.from(files))
    const tagged = type ? uploaded.map((d) => ({ ...d, type })) : uploaded
    form.setFieldValue('documents', [...form.getFieldValue('documents'), ...tagged])
  }

  // Subscribe to values needed in JSX
  const isDirty: boolean = useStore(form.store, (s) => s.isDirty)

  const isSubmitting: boolean = useStore(form.store, (s) => s.isSubmitting)

  // Published rather than duplicated: an external action bar calls exactly the
  // handlers the sticky one does, so there is one implementation of saving.
  useEffect(() => {
    if (!actionsRef) return
    actionsRef.current = { submit: () => void form.handleSubmit(), discard: handleDiscard }
    return () => {
      actionsRef.current = null
    }
  })
  useEffect(() => {
    onStatusChange?.({ isDirty, isSubmitting })
  }, [isDirty, isSubmitting, onStatusChange])

  const isFundraising: boolean = useStore(form.store, (s) => s.values.isFundraising)
  const tags: string[] = useStore(form.store, (s) => s.values.tags)
  const team: DataRoomTeamMember[] = useStore(form.store, (s) => s.values.team)
  const documents: DataRoomDocument[] = useStore(form.store, (s) => s.values.documents)

  // Tag helpers
  const addTag = () => {
    const trimmed = newTag.trim()
    if (trimmed && !tags.includes(trimmed)) {
      form.setFieldValue('tags', [...tags, trimmed])
      setNewTag('')
    }
  }

  // Team helpers
  const addTeamMember = () => {
    form.setFieldValue('team', [
      ...team,
      { id: crypto.randomUUID(), initials: '', name: '', role: '', linkedInUrl: '' },
    ])
  }

  const updateTeamMember = (id: string | undefined, key: string, value: string) => {
    form.setFieldValue(
      'team',
      team.map((m: DataRoomTeamMember) => (m.id === id ? { ...m, [key]: value } : m))
    )
  }

  const removeTeamMember = (id: string | undefined) => {
    form.setFieldValue(
      'team',
      team.filter((m: DataRoomTeamMember) => m.id !== id)
    )
  }

  const validateTeamField = (id: string | undefined, key: string, value: string) => {
    const errorKey = `${key}_${id}`
    let error: string | null = null
    if (key === 'name') error = validateRequired(value, 'Name')
    else if (key === 'role') error = validateRequired(value, 'Role')
    else if (key === 'linkedInUrl') error = validateLinkedInUrl(value)
    setTeamErrors((prev) => {
      if (error) return { ...prev, [errorKey]: error }
      const { [errorKey]: _, ...rest } = prev
      return rest
    })
  }

  const sectorOpts = categoryOptions ?? SECTOR_OPTIONS_FALLBACK
  const stageOpts = stageOptions ?? STAGE_OPTIONS_FALLBACK
  const raiseStageOpts = raiseStageOptions ?? RAISE_STAGE_OPTIONS_FALLBACK

  return (
    <div className="relative">
      {/* Form content */}
      <div className="space-y-10 py-8">
        {/* ── Basic Info ── */}
        <FormSection title="Basic Information" description="How your startup appears to investors">
          <div className="space-y-6">
            {/* Company name + Sector */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field
                name="name"
                validators={{ onBlur: ({ value }) => validateRequired(value, 'Company name') }}
              >
                {(field) => (
                  <Field label="Company name" error={field.state.meta.errors[0]?.toString()}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </Field>
                )}
              </form.Field>
              <div data-focus-key="category">
                <form.Field name="sector">
                  {(field) => (
                    <Field label="Sector / Category">
                      <Select value={field.state.value} onValueChange={field.handleChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select sector..." />
                        </SelectTrigger>
                        <SelectContent>
                          {sectorOpts.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </form.Field>
              </div>
            </div>

            {/* Product stage pills */}
            <div data-focus-key="product_stage">
              <form.Field name="stage">
                {(field) => (
                  <Field label="Product stage">
                    <PillSelect
                      options={stageOpts}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  </Field>
                )}
              </form.Field>
            </div>

            {/* Context fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div data-focus-key="location">
                <form.Field name="location">
                  {(field) => (
                    <Field label="Location">
                      <Input
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="e.g. Prague, Czech Republic"
                      />
                    </Field>
                  )}
                </form.Field>
              </div>
              <div data-focus-key="founding_date">
                <form.Field
                  name="founded"
                  validators={{ onChange: ({ value }) => validateMonth(value) }}
                >
                  {(field) => (
                    <Field label="Founded" error={field.state.meta.errors[0]?.toString()}>
                      <MonthYearSelect
                        value={field.state.value}
                        onChange={(v) => {
                          field.handleChange(v)
                          field.handleBlur()
                        }}
                      />
                    </Field>
                  )}
                </form.Field>
              </div>
              <div data-focus-key="website">
                <form.Field
                  name="website"
                  validators={{ onBlur: ({ value }) => validateHttpsUrl(value) }}
                >
                  {(field) => (
                    <Field label="Website" error={field.state.meta.errors[0]?.toString()}>
                      <Input
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="https://..."
                      />
                    </Field>
                  )}
                </form.Field>
              </div>
            </div>
          </div>
        </FormSection>

        <hr className="border-border" />

        {/* ── Description ── */}
        <div data-focus-key="description">
          <FormSection title="Description" description="Tell investors what you're building">
            <form.Field name="description">
              {(field) => (
                <Field label="About your startup">
                  <Textarea
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    rows={5}
                    placeholder="What does your startup do? What problem does it solve?"
                  />
                </Field>
              )}
            </form.Field>
            <Field label="Tags">
              <div className="space-y-2">
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <Tag
                        key={tag}
                        label={tag}
                        onRemove={() =>
                          form.setFieldValue(
                            'tags',
                            tags.filter((t: string) => t !== tag)
                          )
                        }
                      />
                    ))}
                  </div>
                )}
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addTag()
                    }
                  }}
                  placeholder="Type a tag and press Enter"
                  className="max-w-xs"
                />
              </div>
            </Field>
          </FormSection>
        </div>

        {/* ── Fundraising ── */}
        {!hiddenSections?.has('fundraising') && (
          <>
            <hr className="border-border" />
            <div data-focus-key="fundraising">
              <FormSection title="Fundraising" description="Are you currently raising?">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isFundraising}
                    onCheckedChange={(v) => form.setFieldValue('isFundraising', v)}
                  />
                  <span className="text-sm text-muted-foreground">
                    {isFundraising ? 'Actively raising' : 'Not currently raising'}
                  </span>
                </div>
                {isFundraising && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <form.Field
                      name="fundraisingRound"
                      validators={{
                        onBlur: ({ value }) =>
                          isFundraising ? validateRequired(value, 'Round') : undefined,
                      }}
                    >
                      {(field) => (
                        <Field label="Round" error={field.state.meta.errors[0]?.toString()}>
                          <Select value={field.state.value} onValueChange={field.handleChange}>
                            <SelectTrigger onBlur={field.handleBlur}>
                              <SelectValue placeholder="Select round..." />
                            </SelectTrigger>
                            <SelectContent>
                              {raiseStageOpts.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      )}
                    </form.Field>
                    <form.Field
                      name="fundraisingTarget"
                      validators={{
                        onBlur: ({ value }) =>
                          isFundraising
                            ? (validateRequired(value, 'Target amount') ??
                              validatePositiveAmount(value))
                            : undefined,
                      }}
                    >
                      {(field) => (
                        <Field label="Target amount" error={field.state.meta.errors[0]?.toString()}>
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="e.g. $500,000"
                          />
                        </Field>
                      )}
                    </form.Field>
                  </div>
                )}
              </FormSection>
            </div>
          </>
        )}

        {/* ── Team ── */}
        {!hiddenSections?.has('team') && (
          <>
            <hr className="border-border" />
            <div data-focus-key="team_members">
              <FormSection title="Team" description="Key people behind your startup">
                <div className="space-y-3">
                  {team.map((member) => {
                    const sortedLogos = member.companyLogos
                      ? [...member.companyLogos].sort((a, b) => a.order - b.order)
                      : []
                    return (
                      <div
                        key={member.id}
                        className="relative rounded-lg border border-border bg-card p-4"
                      >
                        <button
                          type="button"
                          onClick={() => removeTeamMember(member.id)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-red-400"
                        >
                          <X className="size-3.5" />
                        </button>
                        <div className="mb-3 flex items-center gap-3">
                          <Avatar className="size-10 shrink-0 bg-elevated">
                            {member.imageUrl && (
                              <AvatarImage src={member.imageUrl} alt={member.name} />
                            )}
                            <AvatarFallback className="bg-elevated text-sm font-semibold text-muted-foreground">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          {sortedLogos.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                              {sortedLogos.map((c) => (
                                <img
                                  key={`${c.order}`}
                                  src={c.logoUrl}
                                  alt=""
                                  className="size-5 rounded object-contain"
                                  loading="lazy"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <Field label="Full name" error={teamErrors[`name_${member.id}`]}>
                            <Input
                              value={member.name}
                              onChange={(e) => updateTeamMember(member.id, 'name', e.target.value)}
                              onBlur={(e) => validateTeamField(member.id, 'name', e.target.value)}
                              placeholder="John Doe"
                            />
                          </Field>
                          <Field label="Role" error={teamErrors[`role_${member.id}`]}>
                            <Input
                              value={member.role}
                              onChange={(e) => updateTeamMember(member.id, 'role', e.target.value)}
                              onBlur={(e) => validateTeamField(member.id, 'role', e.target.value)}
                              placeholder="e.g. CTO"
                            />
                          </Field>
                          <Field label="LinkedIn" error={teamErrors[`linkedInUrl_${member.id}`]}>
                            {(() => {
                              const canRefresh =
                                !!onRefreshMember &&
                                !!member.linkedInUrl &&
                                !!member.id &&
                                !!persistedMemberIds?.has(member.id)
                              const isRefreshing =
                                (!!member.id && refreshingMemberId === member.id) ||
                                member.linkedinScrapeStatus === 'SCRAPING'
                              return (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={member.linkedInUrl ?? ''}
                                    onChange={(e) =>
                                      updateTeamMember(member.id, 'linkedInUrl', e.target.value)
                                    }
                                    // Rewrite what they typed into the canonical
                                    // profile URL as they leave the field, so
                                    // the stored value is one shape and they can
                                    // see what was understood.
                                    onBlur={(e) => {
                                      const canonical = normalizeLinkedInUrl(e.target.value)
                                      if (canonical && canonical !== e.target.value) {
                                        updateTeamMember(member.id, 'linkedInUrl', canonical)
                                      }
                                      validateTeamField(member.id, 'linkedInUrl', e.target.value)
                                    }}
                                    placeholder="johnydoe, or paste your profile URL"
                                  />
                                  {canRefresh && (
                                    <button
                                      type="button"
                                      aria-label="Refresh LinkedIn data"
                                      disabled={isRefreshing}
                                      onClick={() => member.id && onRefreshMember?.(member.id)}
                                      className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      <RefreshCw
                                        className={cn('size-3.5', isRefreshing && 'animate-spin')}
                                      />
                                    </button>
                                  )}
                                </div>
                              )
                            })()}
                          </Field>
                        </div>
                      </div>
                    )
                  })}
                  <SecondaryButton size="sm" icon={<Plus />} onClick={addTeamMember}>
                    Add team member
                  </SecondaryButton>
                </div>
              </FormSection>
            </div>
          </>
        )}

        {/* ── Documents ── */}
        {!hiddenSections?.has('documents') && (
          <>
            <hr className="border-border" />
            <div data-focus-key="pitch_deck">
              <FormSection title="Documents" description="Pitch deck and supporting materials">
                {/* Pitch deck */}
                {(() => {
                  const pitchDeck = documents.find((d) => d.type === 'PITCH_DECK')
                  return (
                    <div className="space-y-3">
                      <Label>Pitch Deck</Label>
                      {pitchDeck ? (
                        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                          <div className="flex size-9 items-center justify-center rounded-lg bg-[#f97316]/10">
                            <FileText className="size-4 text-[#f97316]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <Input
                              value={pitchDeck.name}
                              onChange={(e) =>
                                form.setFieldValue(
                                  'documents',
                                  documents.map((d) =>
                                    d.type === 'PITCH_DECK' ? { ...d, name: e.target.value } : d
                                  )
                                )
                              }
                              className="h-7 max-w-xs border-none bg-transparent px-0 text-sm font-medium text-foreground shadow-none focus-visible:ring-1"
                            />
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <span>{pitchDeck.size}</span>
                              {pitchDeck.createdAt && (
                                <>
                                  <span>·</span>
                                  <span>{formatDate(pitchDeck.createdAt, { month: 'short' })}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="ml-auto flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => onPreviewDocument?.(pitchDeck)}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white"
                            >
                              <Eye className="size-3.5" />
                              Preview
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                form.setFieldValue(
                                  'documents',
                                  documents.filter((d) => d.type !== 'PITCH_DECK')
                                )
                              }
                              className="text-xs text-muted-foreground hover:text-red-400"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <input
                            ref={pitchDeckInputRef}
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={(e) => {
                              handleFileUpload(e.target.files, 'PITCH_DECK')
                              e.target.value = ''
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => pitchDeckInputRef.current?.click()}
                            className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-6 py-6 transition-colors hover:border-[#555]"
                          >
                            <Upload className="size-5 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground">
                              Upload pitch deck (PDF, up to 50MB)
                            </p>
                          </button>
                        </>
                      )}
                    </div>
                  )
                })()}

                {/* Other documents */}
                <div className="space-y-3">
                  <Label>Supporting Documents</Label>
                  {documents
                    .filter((d) => d.type !== 'PITCH_DECK')
                    .map((doc) => (
                      <div
                        key={doc.documentId ?? doc.name}
                        className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
                      >
                        <div className="flex size-9 items-center justify-center rounded-lg bg-card">
                          <FileText className="size-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <Input
                            value={doc.name}
                            onChange={(e) =>
                              form.setFieldValue(
                                'documents',
                                documents.map((d) =>
                                  d.documentId === doc.documentId
                                    ? { ...d, name: e.target.value }
                                    : d
                                )
                              )
                            }
                            className="h-7 max-w-xs border-none bg-transparent px-0 text-sm font-medium text-foreground shadow-none focus-visible:ring-1"
                          />
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span>{doc.size}</span>
                            {doc.createdAt && (
                              <>
                                <span>·</span>
                                <span>{formatDate(doc.createdAt, { month: 'short' })}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => onPreviewDocument?.(doc)}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white"
                          >
                            <Eye className="size-3.5" />
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              form.setFieldValue(
                                'documents',
                                documents.filter((d) => d.documentId !== doc.documentId)
                              )
                            }
                            className="text-xs text-muted-foreground hover:text-red-400"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  <input
                    ref={documentsInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.pptx,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => {
                      handleFileUpload(e.target.files)
                      e.target.value = ''
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => documentsInputRef.current?.click()}
                    className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-6 py-4 transition-colors hover:border-[#555]"
                  >
                    <Upload className="size-5 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">Upload documents</p>
                  </button>
                </div>
              </FormSection>
            </div>
          </>
        )}
      </div>

      {/* Action bar — sticky at bottom. Always rendered when it is this form's
          job: it carries the only way out of edit mode, and hiding it until
          something changed left a founder who opened the form by mistake with
          no cancel and no save. */}
      {!hideActionBar && (
        <div className="sticky bottom-4 z-10 mt-8 rounded-xl border border-border/50 backdrop-blur-md">
          <div className="flex items-center justify-between px-6 py-3">
            {/* The message is about pending edits, so it only appears once
                there are some — the bar itself is always there to leave by. */}
            <p className="text-sm text-muted-foreground">{isDirty ? barMessage : ''}</p>
            <div className="flex items-center gap-2">
              {discardLabel && (
                <SecondaryButton size="sm" onClick={handleDiscard}>
                  {discardLabel}
                </SecondaryButton>
              )}
              <CtaButton size="sm" loading={isSubmitting} onClick={() => form.handleSubmit()}>
                {submitLabel}
              </CtaButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
